import { useState, useEffect, useContext } from 'react';
import { CircularProgress, Modal, Tooltip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Close, Edit, Check, ViewModule, ViewSidebar, ViewWeek, ViewArray, Lock, LockOpen } from '@mui/icons-material';
import Select from 'react-select';
import { twMerge } from 'tailwind-merge';
import { YardLayoutType } from '../../types/Facility';
import { isAdmin } from '../../utils/roleHelper';
import { UserContext } from '../../App';

import { getAuthToken } from '../../utils/cookieUtils';
// Add GraphQL API URL constant
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// @ts-ignore
// Types for dock doors and yard spots
type Status = 'available' | 'in-use' | 'unavailable';

type SpotType = 'dock' | 'yard';

// Dock doors have inbound/outbound groups
type DockGroup = 'inbound' | 'outbound' | 'unassigned';

// Yard spots are organized by zones
type YardZone = {
  id: string;
  name: string;
  color: string; // CSS color for visual indication
  zone_type: 'staging' | 'default'; // Zone type - only one can be staging
};

// Each spot is either a dock door or a yard spot with shared properties
type BaseSpot = {
  id: string;
  number: string;
  type: SpotType;
  status: Status;
  order_number: number; // Add order_number for sorting within zones
};

// Dock door extends base spot with a group
type DockDoor = BaseSpot & {
  type: 'dock';
  group: DockGroup;
};

// Yard spot extends base spot with a zone
type YardSpot = BaseSpot & {
  type: 'yard';
  zoneId: string;
  notes?: string;
};

// Union type of both spot types
type Spot = DockDoor | YardSpot;

// Define layout types
type LayoutType = 'one-sided' | 'two-sided';

// Define props for the YardManagement component
interface YardManagementProps {
  yardLayouts?: YardLayoutType[];
  selectedLayoutId?: string | null;
  onLayoutChange?: (layoutId: string) => void;
  onCancel?: () => void;
}

// Add the GraphQL mutation for updating facility zones
const UPDATE_FACILITY_ZONE_MUTATION = `
  mutation UpdateFacilityZone($uuid: String!, $input: FacilityZoneUpdateInput!) {
    updateFacilityZone(
      uuid: $uuid,
      input: $input
    ) {
      uuid
      name
      zone_type
    }
  }
`;

// Add the GraphQL mutation for updating yard spots
const UPDATE_YARD_SPOT_MUTATION = `
  mutation UpdateYardSpot($uuid: String!, $input: YardSpotUpdateInput!) {
    update_yard_spot(
      uuid: $uuid,
      input: $input
    ) {
      uuid
      name
      facility_id
      facility_zone_uuid
      configuration
    }
  }
`;

// Add the GraphQL mutation for adding new yard spots
const ADD_YARD_SPOT_MUTATION = `
  mutation AddYardSpot($input: YardSpotInput!) {
    add_yard_spot(
      input: $input
    ) {
      uuid
      name
      facility_id
      facility_zone_uuid
      configuration
      created_at
    }
  }
`;

// Add the GraphQL mutation for adding new facility zones
const ADD_FACILITY_ZONE_MUTATION = `
  mutation AddFacilityZone($input: FacilityZoneInput!) {
    add_facility_zone(
      input: $input
    ) {
      uuid
      name
      facility_id
      description
      parent_zone_uuid
      depth
      zone_type
      configuration
      created_at
    }
  }
`;

const YardManagement = ({ yardLayouts, selectedLayoutId, onLayoutChange, onCancel }: YardManagementProps = {}) => {
  // @ts-ignore
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [spots, setSpots] = useState<Spot[]>([]);
  const [selectedSpot, setSelectedSpot] = useState<Spot | null>(null);
  const [showSpotModal, setShowSpotModal] = useState(false);
  const [showYardEditModal, setShowYardEditModal] = useState(false);
  const [showSpotEditModal, setShowSpotEditModal] = useState(false);
  const [showZoneEditModal, setShowZoneEditModal] = useState(false);
  const [showSpotStatusModal, setShowSpotStatusModal] = useState(false);
  const [editingYardSpot, setEditingYardSpot] = useState<YardSpot | null>(null);
  const [originalYardSpotStatus, setOriginalYardSpotStatus] = useState<Status | null>(null);
  const [spotNotesValue, setSpotNotesValue] = useState('');
  const [spotLocks, setSpotLocks] = useState<any[]>([]);
  const [loadingLocks, setLoadingLocks] = useState(false);
  const [clearingLock, setClearingLock] = useState(false);
  const [allFacilityLocks, setAllFacilityLocks] = useState<any[]>([]);
  const [editingZone, setEditingZone] = useState<YardZone | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
  const [layout, setLayout] = useState<LayoutType>('two-sided');
  const [dockCount, setDockCount] = useState(20);
  const [yardCount, setYardCount] = useState(30);
  const [editingSpot, setEditingSpot] = useState<Spot | null>(null);
  const [inboundDockPercentage, setInboundDockPercentage] = useState(50);
  const [showStagingArea, setShowStagingArea] = useState(true);
  // State for tracking zone changes to be saved
  const [pendingZoneChanges, setPendingZoneChanges] = useState<Record<string, any>>({});
  const [pendingSpotChanges, setPendingSpotChanges] = useState<Record<string, any>>({});
  const [savingChanges, setSavingChanges] = useState(false);
  // Occupancy awareness for config UI
  const [occupiedSpotIds, setOccupiedSpotIds] = useState<Set<string>>(new Set());
  const [occupiedDockIds, setOccupiedDockIds] = useState<Set<string>>(new Set());
  
  // Drag and drop state (only within zones)
  const [draggedSpot, setDraggedSpot] = useState<Spot | null>(null);

  // Define zones for yard spots
  const [zones, setZones] = useState<YardZone[]>([
    { id: 'zone-1', name: 'Zone 1', color: '#3b82f6', zone_type: 'staging' }, // blue - make this staging for testing
    { id: 'zone-2', name: 'Zone 2', color: '#f97316', zone_type: 'default' }
  ]);

  // Track which zones came from the API (have valid UUIDs)
  const [originalZones, setOriginalZones] = useState<string[]>([]);

  // Add back the useEffect hook for initial data loading
  useEffect(() => {
    setLoading(true);
    
    // If yard layouts are provided, use them to initialize the component
    if (yardLayouts && yardLayouts.length > 0) {
      processYardLayoutData(yardLayouts, selectedLayoutId);
      
      // Fetch facility locks
      const selectedLayout = selectedLayoutId ? 
        yardLayouts.find(l => l.uuid === selectedLayoutId) : yardLayouts[0];
      if (selectedLayout?.facility_id) {
        fetchAllFacilityLocks(selectedLayout.facility_id);
      }
    } else {
      // Otherwise, fallback to the mock data generation
      generateLayoutData();
    }
    
    // Load occupancy data for main view
    loadOccupancy();
  }, [yardLayouts, selectedLayoutId, layout]); // Dependencies include the props
  
  // Process yard layout data from props
  const processYardLayoutData = (layouts: YardLayoutType[], selectedId: string | null | undefined) => {
    try {
      if (!layouts || layouts.length === 0) {
        throw new Error('No layout data provided');
      }
      
      // Find the selected layout or use the first one
      const selectedLayout = selectedId ? 
        layouts.find(l => l.uuid === selectedId) : layouts[0];
        
      if (!selectedLayout) {
        throw new Error('Selected layout not found');
      }
      
      // Extract zones from the layouts and track which ones came from API
      const extractedZones: YardZone[] = layouts
        .filter(layout => layout.depth !== '0') // Filter out the main yard
        .map((zone, index) => ({
          id: zone.uuid,
          name: zone.name,
          color: zone.configuration?.color || getColorForZone(index), // Use color from configuration or fallback
          zone_type: zone.zone_type || 'default' // Get zone_type from zone level, not configuration
        }));
      
      setZones(extractedZones);
      setOriginalZones(extractedZones.map(z => z.id)); // Track original zone IDs
      
      // Reset pending changes when loading new data
      setPendingZoneChanges({});
      setPendingSpotChanges({});
      
      // Convert yard spots to the format expected by the component
      const extractedSpots: Spot[] = [];
      
      // Add dock doors (we could get this from facility data if available)
      const inboundDockCount = Math.ceil((dockCount * inboundDockPercentage) / 100);
      
      for (let i = 0; i < dockCount; i++) {
        extractedSpots.push({
          id: `dock-${i + 1}`,
          number: `D${i + 1}`,
          type: 'dock' as const,
          status: 'available',
          group: i < inboundDockCount ? 'inbound' : 'outbound',
          order_number: i + 1
        });
      }
      
      // Add yard spots from the layouts
      const allYardSpots: any[] = [];
      layouts.forEach(layout => {
        if (layout.yard_spots && layout.yard_spots.length > 0) {
          layout.yard_spots.forEach(spot => {
            allYardSpots.push(spot);
          });
        }
      });

      // Sort yard spots by order_number and add them to extractedSpots
      allYardSpots
        .sort((a, b) => {
          const orderA = a.order_number ? parseInt(a.order_number.toString()) : 999999;
          const orderB = b.order_number ? parseInt(b.order_number.toString()) : 999999;
          return orderA - orderB;
        })
        .forEach((spot, index) => {
          // Parse configuration to get status and notes
          let configStatus: Status = 'available';
          let configNotes = '';
          
          if (spot.configuration) {
            try {
              const config = typeof spot.configuration === 'string' ? 
                JSON.parse(spot.configuration) : spot.configuration;
              configStatus = (config.status as Status) || 'available';
              configNotes = config.notes || '';
            } catch (error) {
              console.error('Error parsing yard spot configuration:', error);
            }
          }
          
          extractedSpots.push({
            id: spot.uuid,
            number: spot.name,
            type: 'yard' as const,
            status: configStatus,
            zoneId: spot.facility_zone_uuid,
            order_number: spot.order_number ? parseInt(spot.order_number.toString()) : index + 1,
            notes: configNotes
          });
        });
      
      setSpots(extractedSpots);
      setLoading(false);
    } catch (error) {
      console.error('Error processing yard layout data:', error);
      // Fallback to mock data generation
      generateLayoutData();
    }
  };
  
  // Helper function to generate colors for zones
  const getColorForZone = (index: number): string => {
    const colors = [
      '#3b82f6', // blue
      '#f97316', // orange
      '#10b981', // green
      '#ef4444', // red
      '#8b5cf6', // purple
      '#f59e0b', // amber
      '#ec4899'  // pink
    ];
    
    return colors[index % colors.length];
  };

  // Modified generateLayoutData with group assignments
  const generateLayoutData = () => {
    // Create a map of existing spots to preserve custom names and status
    const existingSpots: Record<string, Spot> = {};
    spots.forEach(spot => {
      existingSpots[spot.id] = spot;
    });
    
    // Calculate how many dock doors should be inbound based on percentage
    const inboundDockCount = Math.ceil((dockCount * inboundDockPercentage) / 100);
    
    // Create mock dock doors based on dockCount
    const dockDoors: DockDoor[] = Array.from({ length: dockCount }, (_, i) => {
      const id = `dock-${i + 1}`;
      const existingSpot = existingSpots[id];
      // Assign group - first N doors are inbound, rest are outbound
      const group: DockGroup = i < inboundDockCount ? 'inbound' : 'outbound';
      
      // Check if existing spot is a dock door to preserve its group
      const existingGroup = existingSpot?.type === 'dock' ? existingSpot.group : undefined;
      
      return {
        id,
        // Preserve existing data if available
        number: existingSpot?.number || `D${i + 1}`,
        type: 'dock' as const,
        status: existingSpot?.status || 'available',
        group: existingGroup || group, // Keep existing group if available, otherwise assign new
        order_number: i + 1
      };
    });
    
    // Create mock yard spots based on yardCount and assign to zones
    const yardSpots: YardSpot[] = Array.from({ length: yardCount }, (_, i) => {
      const id = `yard-${i + 1}`;
      const existingSpot = existingSpots[id];
      
      // Distribute yard spots across zones evenly (cyclic assignment)
      const zoneIndex = i % zones.length;
      const zoneId = zones[zoneIndex].id;
      
      // Check if existing spot is a yard spot to preserve its zoneId
      const existingZoneId = existingSpot?.type === 'yard' ? existingSpot.zoneId : undefined;
      
      return {
        id,
        // Preserve existing data if available
        number: existingSpot?.number || `Y${i + 1}`,
        type: 'yard' as const,
        status: existingSpot?.status || 'available',
        zoneId: existingZoneId || zoneId, // Keep existing zoneId if available, otherwise assign new
        order_number: i + 1
      };
    });

    setSpots([...dockDoors, ...yardSpots]);
    setLoading(false);
  };

  const handleSpotClick = async (spot: Spot) => {
    // Open status change modal
    setSelectedSpot(spot);
    setOriginalYardSpotStatus(spot.status);
    setSpotNotesValue((spot as YardSpot).notes || '');
    setShowSpotStatusModal(true);

    // Fetch locks for this spot
    await fetchSpotLocks(spot.id);
  };

  const handleSpotModalClose = () => {
    setShowSpotModal(false);
    setSelectedSpot(null);
  };

  const handleSpotStatusModalClose = () => {
    setShowSpotStatusModal(false);
    setSelectedSpot(null);
    setOriginalYardSpotStatus(null);
    setSpotNotesValue('');
  };

  const handleSpotStatusSave = async () => {
    if (!selectedSpot || selectedSpot.type !== 'yard') return;

    const statusChanged = selectedSpot.status !== originalYardSpotStatus;
    const notesChanged = spotNotesValue !== ((selectedSpot as YardSpot).notes || '');

    if (statusChanged) {
      const confirmed = window.confirm('Are you sure you want to change the status?');
      if (!confirmed) return;
    }

    if (!statusChanged && !notesChanged) {
      handleSpotStatusModalClose();
      return;
    }

    try {
      setLoading(true);

      const authToken = getAuthToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      // Update local state
      const updatedSpot = { ...selectedSpot, notes: spotNotesValue || undefined } as Spot;
      setSpots(spots.map(spot =>
        spot.id === selectedSpot.id ? updatedSpot : spot
      ));

      // Execute mutation - preserve existing config fields
      const input = {
        name: selectedSpot.number,
        facility_zone_uuid: selectedSpot.zoneId,
        order_number: String(selectedSpot.order_number),
        configuration: JSON.stringify({ status: selectedSpot.status, notes: spotNotesValue || null })
      };

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: UPDATE_YARD_SPOT_MUTATION,
          variables: {
            uuid: selectedSpot.id,
            input
          }
        })
      });

      const result = await response.json();

      if (result.errors) {
        console.error('Error updating yard spot status:', result.errors);
        throw new Error(result.errors[0].message);
      }

      console.log('Yard spot status updated:', result.data.update_yard_spot);
      alert('Status updated successfully!');
      handleSpotStatusModalClose();
      
    } catch (error: any) {
      console.error('Error updating yard spot status:', error);
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all facility locks
  const fetchAllFacilityLocks = async (facilityId: string) => {
    try {
      const authToken = getAuthToken();

      if (!authToken) return;

      const facilityLocksQuery = `
        query {
          facilityLocks(facilityId: "${facilityId}") {
            reason
            resource_uuid
            model_name
            operation_type
            created_by
            task_uuid
            created_at
            expires_at
            namespace
            facility_id
            resource {
              ... on YardSpotObject {
                __typename
                uuid
                name
              }
              ... on DockObject {
                __typename
                uuid
                name
              }
              ... on Trailer {
                __typename
                uuid
                trailerNumber
              }
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: facilityLocksQuery
        })
      });

      const result = await response.json();
      
      if (result.data && result.data.facilityLocks) {
        setAllFacilityLocks(result.data.facilityLocks);
      }
    } catch (error) {
      console.error('Error fetching facility locks:', error);
    }
  };

  // Fetch locks for a specific spot (includes both location and trailer locks)
  const fetchSpotLocks = async (spotId: string) => {
    setLoadingLocks(true);
    try {
      // Get locks on the location itself
      const locationLocks = allFacilityLocks.filter(lock => lock.resource_uuid === spotId);
      
      // Note: We only show location locks here. Trailer locks are indirectly shown
      // since a locked trailer at a spot prevents operations on that spot.
      // To show trailer locks, we'd need to match trailers to spots from yard data,
      // which is already done in yardDataFetch.ts
      
      setSpotLocks(locationLocks);
    } catch (error) {
      console.error('Error fetching spot locks:', error);
    } finally {
      setLoadingLocks(false);
    }
  };

  // Clear a lock
  const handleClearLock = async (resourceUuid: string, taskUuid: string | null) => {
    setClearingLock(true);
    try {
      const authToken = getAuthToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      const clearLockMutation = `
        mutation ReleaseTaskLocks($taskUuid: String!) {
          releaseTaskLocks(taskUuid: $taskUuid) {
            success
            message
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: clearLockMutation,
          variables: { taskUuid: taskUuid || resourceUuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      alert('Lock cleared successfully!');
      
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error: any) {
      console.error('Error clearing lock:', error);
      alert(`Failed to clear lock: ${error.message}`);
      setClearingLock(false);
    }
  };

  // Check if a spot has locks
  const spotHasLock = (spotId: string) => {
    return allFacilityLocks.some(lock => lock.resource_uuid === spotId);
  };

  const handleStatusChange = (status: Status) => {
    if (selectedSpot) {
      const updatedSpots = spots.map(spot => 
        spot.id === selectedSpot.id ? { ...spot, status } : spot
      );
      setSpots(updatedSpots);
      setSelectedSpot({ ...selectedSpot, status });
    }
  };

  const handleLayoutChange = (event: React.MouseEvent<HTMLElement>, newLayout: LayoutType) => {
    if (newLayout !== null) {
      setLayout(newLayout);
    }
  };

  // Helper to load current yard and warehouse occupancy so we can reflect lock state in the UI
  const loadOccupancy = async () => {
    try {
      const activeFacilityId = yardLayouts && yardLayouts.length > 0
        ? yardLayouts.find(l => l.uuid === (selectedLayoutId || yardLayouts[0]?.uuid))?.facility_id
        : undefined;
      if (!activeFacilityId) return;

      const authToken = getAuthToken();

      const occupancyQuery = `
        query GetOccupancy {
          yard(facility_id: "${activeFacilityId}") {
            facility_zones {
              yard_spots {
                yard_spot_uuid
                yard_spot_status
                trailer_uuid
              }
            }
          }
          warehouse(facilityId: "${activeFacilityId}") {
            warehouseZones {
              zoneUuid
              docks {
                dockUuid
                dockName
                dockStatus
                trailer_name
                configuration
              }
            }
          }
        }
      `;
      const resp = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ query: occupancyQuery })
      });
      const data = await resp.json();
      if (data.errors) return;
      const nextSpots = new Set<string>();
      data?.data?.yard?.facility_zones?.forEach((zone: any) => {
        (zone.yard_spots || []).forEach((spot: any) => {
          const hasTrailer = !!spot.trailer_uuid;
          const occupiedByStatus = String(spot.yard_spot_status || '').toUpperCase() === 'OCCUPIED' || String(spot.yard_spot_status || '').toLowerCase() === 'in-use';
          if (hasTrailer || occupiedByStatus) nextSpots.add(spot.yard_spot_uuid);
        });
      });
      setOccupiedSpotIds(nextSpots);

      const nextDocks = new Set<string>();
      data?.data?.warehouse?.warehouseZones?.forEach((zone: any) => {
        (zone.docks || []).forEach((dock: any) => {
          const hasTrailer = !!dock.trailer_name;
          const occupiedByStatus = String(dock.dockStatus || '').toLowerCase() === 'in-use' || String(dock.configuration?.status || '').toLowerCase() === 'unavailable';
          if (hasTrailer || occupiedByStatus) nextDocks.add(dock.dockUuid);
        });
      });
      setOccupiedDockIds(nextDocks);
    } catch (e) {
      console.warn('Failed to load occupancy for UI lock hints', e);
    }
  };

  const handleYardEditOpen = () => {
    loadOccupancy();
    setShowYardEditModal(true);
  };

  const handleYardEditClose = () => {
    setShowYardEditModal(false);
    setEditingSpot(null);
    onCancel?.();
  };

  const handleYardEditSave = async () => {
    setSavingChanges(true);
    
    try {
      // Determine facility id from provided layouts
      const activeFacilityId = yardLayouts && yardLayouts.length > 0
        ? yardLayouts.find(l => l.uuid === (selectedLayoutId || yardLayouts[0]?.uuid))?.facility_id
        : undefined;

      // Get auth token from cookie
      const authToken = getAuthToken();
      
      // Preflight: fetch current yard occupancy to enforce config rule
      const occupiedSpotIds = new Set<string>();
      if (activeFacilityId) {
        try {
          const occupancyQuery = `
            query GetYardOccupancy {
              yard(facility_id: "${activeFacilityId}") {
                facility_zones {
                  yard_spots {
                    yard_spot_uuid
                    yard_spot_status
                    trailer_uuid
                  }
                }
              }
            }
          `;
          const occResponse = await fetch(GRAPHQL_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({ query: occupancyQuery })
          });
          const occResult = await occResponse.json();
          if (!occResult.errors && occResult.data?.yard?.facility_zones) {
            occResult.data.yard.facility_zones.forEach((zone: any) => {
              (zone.yard_spots || []).forEach((spot: any) => {
                const hasTrailer = !!spot.trailer_uuid;
                const occupiedByStatus = String(spot.yard_spot_status || '').toUpperCase() === 'OCCUPIED' || String(spot.yard_spot_status || '').toLowerCase() === 'in-use';
                if (hasTrailer || occupiedByStatus) {
                  occupiedSpotIds.add(spot.yard_spot_uuid);
                }
              });
            });
          }
        } catch (e) {
          // If occupancy fetch fails, proceed without enforcement rather than blocking saves
          console.warn('Yard occupancy preflight failed; proceeding without occupancy enforcement', e);
        }
      }

      // Helper: safe JSON parse
      const safeParseJson = (value: any) => {
        try {
          return typeof value === 'string' ? JSON.parse(value) : (value || {});
        } catch {
          return {};
        }
      };

      // Build batch mutation query and variables
      const mutations: string[] = [];
      const variables: Record<string, any> = {};
      let mutationIndex = 0;
      
      // Process zone changes
      for (const [zoneId, changes] of Object.entries(pendingZoneChanges)) {
        const varName = `mutation${mutationIndex}`;
        mutationIndex++;
        
        if (changes.isNew) {
          // New zone mutation
          const input = {
            name: changes.name,
            facility_id: changes.facility_id,
            description: changes.description,
            parent_zone_uuid: changes.parent_zone_uuid,
            depth: "1",
            ...(changes.zone_type && { zone_type: changes.zone_type }),
            ...(changes.configuration && { configuration: JSON.stringify(changes.configuration) })
          };
          
          mutations.push(`
            ${varName}: add_facility_zone(input: $${varName}) {
              uuid
              name
              facility_id
              description
              parent_zone_uuid
              depth
              zone_type
              configuration
              created_at
            }
          `);
          variables[varName] = input;
        } else {
          // Update zone mutation
          const input = {
            name: changes.name,
            description: changes.description,
            depth: "1",
            ...(changes.zone_type && { zone_type: changes.zone_type }),
            ...(changes.configuration && { configuration: JSON.stringify(changes.configuration) })
          };
          
          mutations.push(`
            ${varName}: updateFacilityZone(uuid: "${zoneId}", input: $${varName}) {
              uuid
              name
              zone_type
            }
          `);
          variables[varName] = input;
        }
      }
      
      // Process spot changes
      for (const [spotId, changes] of Object.entries(pendingSpotChanges)) {
        const varName = `mutation${mutationIndex}`;
        mutationIndex++;
        
        if (changes.isNew) {
          // New spot mutation
          const input = {
            name: changes.name,
            facility_id: changes.facility_id,
            facility_zone_uuid: changes.facility_zone_uuid,
            order_number: changes.order_number ? String(changes.order_number) : "1",
            ...(changes.configuration && { configuration: JSON.stringify(changes.configuration) })
          };
          
          mutations.push(`
            ${varName}: add_yard_spot(input: $${varName}) {
              uuid
              name
              facility_id
              facility_zone_uuid
              configuration
              created_at
            }
          `);
          variables[varName] = input;
        } else {
          // Update spot mutation
          // Enforce: if a trailer is present at this spot, only allow configuration.status to be 'available'
          const originalConfig = typeof changes.configuration === 'string'
            ? safeParseJson(changes.configuration)
            : (changes.configuration || {});
          const adjustedConfig = { ...originalConfig } as any;
          if (
            occupiedSpotIds.has(spotId) &&
            adjustedConfig.status && String(adjustedConfig.status).toLowerCase() !== 'available'
          ) {
            adjustedConfig.status = 'available';
            console.warn(`Forcing yard spot ${spotId} configuration.status to 'available' because a trailer is present.`);
          }

          const input = {
            name: changes.name,
            facility_zone_uuid: changes.facility_zone_uuid,
            order_number: changes.order_number ? String(changes.order_number) : undefined,
            ...(Object.keys(adjustedConfig).length > 0 && { configuration: JSON.stringify(adjustedConfig) })
          };
          
          mutations.push(`
            ${varName}: update_yard_spot(uuid: "${spotId}", input: $${varName}) {
              uuid
              name
              facility_id
              facility_zone_uuid
              configuration
            }
          `);
          variables[varName] = input;
        }
      }
      
      // Only make the API call if there are mutations to execute
      if (mutations.length > 0) {
        const batchQuery = `
          mutation BatchUpdate(${Object.keys(variables).map(key => {
            // Determine the input type based on the mutation
            const mutation = mutations.find(m => m.includes(key));
            if (mutation?.includes('add_facility_zone')) {
              return `$${key}: FacilityZoneInput!`;
            } else if (mutation?.includes('updateFacilityZone')) {
              return `$${key}: FacilityZoneUpdateInput!`;
            } else if (mutation?.includes('add_yard_spot')) {
              return `$${key}: YardSpotInput!`;
            } else if (mutation?.includes('update_yard_spot')) {
              return `$${key}: YardSpotUpdateInput!`;
            }
            return `$${key}: String!`; // fallback
          }).join(', ')}) {
            ${mutations.join('\n')}
          }
        `;
        
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: batchQuery,
            variables
          })
        });
        
        const result = await response.json();
        
        if (result.errors) {
          console.error('Error in batch mutation:', result.errors);
          throw new Error(result.errors[0].message);
        }
        
        console.log('Batch mutation completed:', result.data);
        
        // Update local state with successful API response data
        if (result.data) {
          const updatedZones = [...zones];
          
          // Update zones with successful responses
          for (const [key, value] of Object.entries(result.data)) {
            if (key.startsWith('mutation') && value && typeof value === 'object') {
              const responseData = value as any;
              
              // Check if this is a zone mutation response
              if (responseData.uuid && responseData.name) {
                const zoneIndex = updatedZones.findIndex(zone => zone.id === responseData.uuid);
                if (zoneIndex !== -1) {
                  updatedZones[zoneIndex] = {
                    ...updatedZones[zoneIndex],
                    name: responseData.name,
                    zone_type: responseData.zone_type || updatedZones[zoneIndex].zone_type
                  };
                }
              }
            }
          }
          
          setZones(updatedZones);
        }
      }
      
      // Clear pending changes after successful save
      setPendingZoneChanges({});
      setPendingSpotChanges({});
      
      // Close the modal after saving
      handleYardEditClose();
      
      // Note: Removed onLayoutChange call to prevent race condition with data refresh
      // The local state is now updated with the API response data directly
      
    } catch (error) {
      console.error('Error saving changes:', error);
      // You could show an error message to the user here
    } finally {
      setSavingChanges(false);
    }
  };

  const handleSpotEdit = (spot: Spot) => {
    setEditingSpot({...spot});
  };

  const handleSpotNameChange = (id: string, newName: string) => {
    // Update the spots array for UI
    setSpots(spots.map(spot => 
      spot.id === id ? { ...spot, number: newName } : spot
    ));

    // Track the change for API update later
    // Only track changes for actual API-sourced spots (those with UUID format)
    if (id.includes('-') && !id.startsWith('dock-') && !id.startsWith('yard-')) {
      setPendingSpotChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: newName
        }
      }));
    }
  };

  const handleDockCountChange = (newCount: number) => {
    setDockCount(Math.max(1, newCount));
  };

  const handleYardCountChange = (newCount: number) => {
    setYardCount(Math.max(1, newCount));
  };

  const getStatusColor = (status: Status): string => {
    switch (status) {
      case 'available':
        return 'bg-green-500';
      case 'in-use':
        return 'bg-red-500';
      case 'unavailable':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Updated to only work with dock doors
  const getGroupColor = (group: DockGroup): string => {
    switch (group) {
      case 'inbound':
        return 'border-blue-500 border-4';
      case 'outbound':
        return 'border-orange-500 border-4';
      default:
        return 'border-gray-400 border';
    }
  };
  
  // New helper to get zone color
  const getZoneColor = (zoneId: string): string => {
    const zone = zones.find(z => z.id === zoneId);
    return zone ? `border-[${zone.color}] border-4` : 'border-gray-400 border';
  };

  // Updated to handle both spot types
  const getSpotTooltip = (spot: Spot): string => {
    if (spot.type === 'dock') {
      const groupText = spot.group.charAt(0).toUpperCase() + spot.group.slice(1);
      return `Door ${spot.number} - ${spot.status} (${groupText})`;
    } else {
      const zone = zones.find(z => z.id === spot.zoneId);
      const zoneText = zone ? zone.name : 'Unassigned Zone';
      return `Spot ${spot.number} - ${spot.status} (${zoneText})`;
    }
  };

  const filteredSpots = statusFilter === 'all' 
    ? spots 
    : spots.filter(spot => spot.status === statusFilter);

  // Update dock group change handler
  const handleDockGroupChange = (id: string, newGroup: DockGroup) => {
    setSpots(spots.map(spot => {
      if (spot.id === id && spot.type === 'dock') {
        return { ...spot, group: newGroup };
      }
      return spot;
    }));
    
    // If modifying the currently selected spot, update that too
    if (selectedSpot && selectedSpot.id === id && selectedSpot.type === 'dock') {
      setSelectedSpot({ ...selectedSpot, group: newGroup });
    }
  };

  // Modify the yard zone change handler to track changes
  const handleYardZoneChange = (id: string, newZoneId: string) => {
    // Update spots array for UI
    setSpots(spots.map(spot => {
      if (spot.id === id && spot.type === 'yard') {
        return { ...spot, zoneId: newZoneId };
      }
      return spot;
    }));
    
    // If modifying the currently selected spot, update that too
    if (selectedSpot && selectedSpot.id === id && selectedSpot.type === 'yard') {
      setSelectedSpot({ ...selectedSpot, zoneId: newZoneId });
    }

    // Track the change for API update later
    // Only track changes for actual API-sourced spots (those with UUID format)
    if (id.includes('-') && !id.startsWith('dock-') && !id.startsWith('yard-')) {
      setPendingSpotChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          facility_zone_uuid: newZoneId
        }
      }));
    }
  };

  // Add zone management
  const handleAddZone = () => {
    // Check if we're editing an existing yard layout
    if (!showYardEditModal) {
      handleYardEditOpen();
      return;
    }

    // Generate a random color with proper hex formatting
    const randomColor = '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
    
    // For UI display, create a temporary zone with client-side ID
    const newZone: YardZone = {
      id: `temp-zone-${Date.now()}`,
      name: `Zone ${zones.length + 1}`,
      color: randomColor,
      zone_type: 'default'
    };
    
    setZones([...zones, newZone]);
    
    // If we have a selected facility and yard layout, prepare to create a new zone in the database
    if (yardLayouts && yardLayouts.length > 0 && selectedLayoutId) {
      const facilityId = yardLayouts.find(l => l.uuid === selectedLayoutId)?.facility_id;
      
      if (facilityId) {
        // Create a new zone entry to be sent to the API on save
        const newZoneInput = {
          name: newZone.name,
          facility_id: facilityId, // Keep as string to preserve big integer precision
          description: `Zone ${zones.length + 1}`,
          parent_zone_uuid: selectedLayoutId, // The parent is the main yard layout
          depth: 1, // Zones are typically depth 1 (one level below the main yard)
          zone_type: 'default', // Move zone_type to top level
          configuration: {
            color: randomColor,
            position: {
              x: 100,
              y: 100
            },
            size: {
              width: 200,
              height: 150
            }
          }
        };
        
        // Store this as a new zone to be created on save
        setPendingZoneChanges(prev => ({
          ...prev,
          [newZone.id]: {
            ...newZoneInput,
            isNew: true // Flag that this is a new zone, not an update
          }
        }));
      }
    }
  };

  // Add new function to handle adding a new yard spot
  const handleAddYardSpot = () => {
    // Must be in edit mode and have at least one zone
    if (!showYardEditModal || zones.length === 0) {
      return;
    }
    
    // Find the first zone as default
    const defaultZoneId = zones[0].id;
    
    // Create a temporary spot for UI display
    const newSpotName = `Spot ${spots.filter(s => s.type === 'yard').length + 1}`;
    const newSpotId = `temp-spot-${Date.now()}`;
    
    // Add to UI spots
    const newSpot: YardSpot = {
      id: newSpotId,
      number: newSpotName,
      type: 'yard',
      status: 'available',
      zoneId: defaultZoneId,
      order_number: spots.filter(s => s.type === 'yard' && (s as YardSpot).zoneId === defaultZoneId).length + 1
    };
    
    setSpots([...spots, newSpot]);
    
    // If we have a selected facility and yard layout, prepare to create a new spot in the database
    if (yardLayouts && yardLayouts.length > 0 && selectedLayoutId) {
      const facilityId = yardLayouts.find(l => l.uuid === selectedLayoutId)?.facility_id;
      
      if (facilityId) {
        // Create a new spot entry to be sent to the API on save
        const newSpotInput = {
          name: newSpotName,
          facility_id: facilityId, // Keep as string to preserve big integer precision
          facility_zone_uuid: defaultZoneId,
          order_number: newSpot.order_number,
          configuration: {
            position: {
              x: 150,
              y: 150
            },
            size: {
              width: 100,
              height: 50
            }
          }
        };
        
        // Store this as a new spot to be created on save
        setPendingSpotChanges(prev => ({
          ...prev,
          [newSpotId]: {
            ...newSpotInput,
            isNew: true // Flag that this is a new spot, not an update
          }
        }));
      }
    }
  };

  // Modify the zone name change handler to track changes
  const handleZoneNameChange = (id: string, newName: string) => {
    // Update the UI state
    setZones(zones.map(zone => 
      zone.id === id ? { ...zone, name: newName } : zone
    ));
    
    // Track the change for later submission
    if (originalZones.includes(id)) {
      // Existing zone - update it
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: newName
        }
      }));
    } else {
      // New zone - update its configuration
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          name: newName
        }
      }));
    }
  };

  // Modify the zone color change handler to track changes
  const handleZoneColorChange = (id: string, newColor: string) => {
    // Update the UI state
    setZones(zones.map(zone => 
      zone.id === id ? { ...zone, color: newColor } : zone
    ));
    
    // Track the color change for both existing and new zones
    if (originalZones.includes(id)) {
      // Existing zone - track in configuration
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          configuration: {
            ...(prev[id]?.configuration || {}),
            color: newColor
          }
        }
      }));
    } else {
      // New zone - update its configuration
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          configuration: {
            ...(prev[id]?.configuration || {}),
            color: newColor
          }
        }
      }));
    }
  };

  // Add handler for zone type changes (staging vs default)
  const handleZoneTypeChange = (id: string, newZoneType: 'staging' | 'default') => {
    // If setting to staging, first set all other zones to default
    let updatedZones = zones;
    if (newZoneType === 'staging') {
      updatedZones = zones.map(zone => 
        zone.id !== id ? { ...zone, zone_type: 'default' } : zone
      );
      
      // Track changes for all zones that were changed to default
      updatedZones.forEach(zone => {
        if (zone.id !== id && zone.zone_type === 'default') {
          if (originalZones.includes(zone.id)) {
            setPendingZoneChanges(prev => ({
              ...prev,
              [zone.id]: {
                ...prev[zone.id],
                zone_type: 'default' // Track zone_type at top level
              }
            }));
          } else {
            setPendingZoneChanges(prev => ({
              ...prev,
              [zone.id]: {
                ...prev[zone.id],
                zone_type: 'default' // Track zone_type at top level
              }
            }));
          }
        }
      });
    }
    
    // Update the UI state for the target zone
    setZones(updatedZones.map(zone => 
      zone.id === id ? { ...zone, zone_type: newZoneType } : zone
    ));
    
    // Track the zone type change for the target zone
    if (originalZones.includes(id)) {
      // Existing zone - track at top level
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          zone_type: newZoneType
        }
      }));
    } else {
      // New zone - track at top level
      setPendingZoneChanges(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          zone_type: newZoneType
        }
      }));
    }
  };

  const handleInboundDockPercentageChange = (newPercentage: number) => {
    setInboundDockPercentage(Math.min(100, Math.max(0, newPercentage)));
  };

  // Add a new function to show pending changes status
  const getPendingChangesCount = () => {
    return Object.keys(pendingZoneChanges).length + Object.keys(pendingSpotChanges).length;
  };

  const handleSpotEditOpen = (spot: YardSpot) => {
    setEditingYardSpot({ ...spot });
    setOriginalYardSpotStatus(spot.status);
    setShowSpotEditModal(true);
  };

  const handleSpotEditClose = () => {
    setShowSpotEditModal(false);
    setEditingYardSpot(null);
    setOriginalYardSpotStatus(null);
  };

  const handleSpotEditSave = () => {
    if (editingYardSpot) {
      // Update the spots array
      setSpots(spots.map(spot => 
        spot.id === editingYardSpot.id ? editingYardSpot : spot
      ));

      // Track the change for API update later
      if (editingYardSpot.id.includes('-') && !editingYardSpot.id.startsWith('dock-') && !editingYardSpot.id.startsWith('yard-')) {
        setPendingSpotChanges(prev => ({
          ...prev,
          [editingYardSpot.id]: {
            ...prev[editingYardSpot.id],
            name: editingYardSpot.number,
            facility_zone_uuid: editingYardSpot.zoneId,
            order_number: editingYardSpot.order_number,
            configuration: {
              ...(prev[editingYardSpot.id]?.configuration || {}),
              status: editingYardSpot.status,
              notes: editingYardSpot.notes
            }
          }
        }));
      }

      handleSpotEditClose();
    }
  };

  const handleZoneEditOpen = (zone: YardZone) => {
    setEditingZone({ ...zone });
    setShowZoneEditModal(true);
  };

  const handleZoneEditClose = () => {
    setShowZoneEditModal(false);
    setEditingZone(null);
  };

  const handleZoneEditSave = async () => {
    if (editingZone) {
      try {
        // Get auth token from cookie
        const authToken = getAuthToken();

        // Prepare the input for the API
        const input = {
          name: editingZone.name,
          description: '',
          depth: "1",
          zone_type: editingZone.zone_type,
          configuration: JSON.stringify({
            color: editingZone.color
          })
        };

        // Call the GraphQL mutation to update the zone
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: UPDATE_FACILITY_ZONE_MUTATION,
            variables: {
              uuid: editingZone.id,
              input
            }
          })
        });

        const result = await response.json();

        if (result.errors) {
          console.error('Error updating facility zone:', result.errors);
          throw new Error(result.errors[0].message);
        }

        console.log('Zone updated successfully:', result.data.updateFacilityZone);

        // Update local state with the successful response
        if (result.data.updateFacilityZone) {
          setZones(prevZones => 
            prevZones.map(zone => 
              zone.id === editingZone.id ? {
                ...zone,
                name: result.data.updateFacilityZone.name,
                zone_type: result.data.updateFacilityZone.zone_type,
                color: editingZone.color
              } : zone
            )
          );
        }

        handleZoneEditClose();
      } catch (error) {
        console.error('Error saving zone changes:', error);
        // You could show an error message to the user here
      }
    }
  };

  // Drag and drop helper functions (within zones only)
  const handleSpotDragStart = (e: React.DragEvent<HTMLDivElement>, spot: Spot) => {
    setDraggedSpot(spot);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.4';
  };

  const handleSpotDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';
    setDraggedSpot(null);
  };

  const handleSpotDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSpotDrop = (e: React.DragEvent<HTMLDivElement>, targetSpot: Spot) => {
    e.preventDefault();
    
    if (!draggedSpot || draggedSpot.id === targetSpot.id) {
      return;
    }

    // Only allow reordering within the same type and same zone
    if (draggedSpot.type !== targetSpot.type) {
      return;
    }

    if (draggedSpot.type === 'yard' && targetSpot.type === 'yard') {
      const draggedYardSpot = draggedSpot as YardSpot;
      const targetYardSpot = targetSpot as YardSpot;
      
      if (draggedYardSpot.zoneId !== targetYardSpot.zoneId) {
        return; // Don't allow reordering across different zones
      }
    }

    // Reorder the spots within the zone
    reorderSpotsWithinZone(draggedSpot, targetSpot);
  };

  const reorderSpotsWithinZone = (draggedSpot: Spot, targetSpot: Spot) => {
    const updatedSpots = [...spots];
    
    // Filter spots of the same type and zone
    let relevantSpots: Spot[];
    if (draggedSpot.type === 'yard') {
      const draggedYardSpot = draggedSpot as YardSpot;
      relevantSpots = updatedSpots.filter(spot => 
        spot.type === 'yard' && (spot as YardSpot).zoneId === draggedYardSpot.zoneId
      );
    } else {
      // For dock doors, all are in the same "zone"
      relevantSpots = updatedSpots.filter(spot => spot.type === 'dock');
    }

    // Sort by current order_number
    relevantSpots.sort((a, b) => a.order_number - b.order_number);

    // Find current indices
    const draggedIndex = relevantSpots.findIndex(spot => spot.id === draggedSpot.id);
    const targetIndex = relevantSpots.findIndex(spot => spot.id === targetSpot.id);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    // Remove dragged spot and insert at target position
    const [removed] = relevantSpots.splice(draggedIndex, 1);
    relevantSpots.splice(targetIndex, 0, removed);

    // Update order_numbers within the zone/type starting from 1
    relevantSpots.forEach((spot, index) => {
      const spotIndex = updatedSpots.findIndex(s => s.id === spot.id);
      if (spotIndex !== -1) {
        updatedSpots[spotIndex] = { ...updatedSpots[spotIndex], order_number: index + 1 };
        
        // Track the change for API update
        if (spot.id.includes('-') && !spot.id.startsWith('dock-') && !spot.id.startsWith('yard-')) {
          setPendingSpotChanges(prev => ({
            ...prev,
            [spot.id]: {
              ...prev[spot.id],
              order_number: index + 1
            }
          }));
        }
      }
    });

    setSpots(updatedSpots);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center mt-4 w-full">
        <CircularProgress />
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'available', label: 'Available' },
    { value: 'in-use', label: 'In Use' },
    { value: 'unavailable', label: 'Unavailable' }
  ];

  return (
    <div className="w-full">
      <div className="flex mb-4 items-center">
        <div className="mr-4">
          {isAdmin(user) && (<button
            className="bg-sky-600 hover:bg-sky-500 text-white px-3 py-1 rounded flex items-center"
            onClick={handleYardEditOpen}
          >
            <Edit fontSize="small" className="mr-1" />
            Edit Yard Layout
          </button>
          )}
        </div>
      
      </div>

      {/* Main yard layout container */}
      <div className="border border-gray-300 bg-gray-100 relative mb-8">
        {/* Use CSS grid for overall layout */}
        <div className={`${layout === 'one-sided' ? 'grid grid-rows-[2fr_1fr]' : 'flex flex-col'} min-h-[550px]`}>
          
          {layout === 'one-sided' ? (
            // One-sided layout (yard above, warehouse below with doors on top)
            <>
              {/* Yard area - Updated to show zones */}
              <div className="bg-green-100 border-2 border-gray-600 p-4 m-4 mb-2 rounded flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-center">Yard</h3>
                  <div className="flex text-xs gap-2">
                    {zones.map(zone => (
                      <div key={zone.id} className="flex items-center">
                        <div className="w-3 h-3 mr-1" style={{ backgroundColor: zone.color }}></div>
                        <span>{zone.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Yard zones layout */}
                <div className="flex-1 grid grid-cols-1 gap-4 auto-rows-max place-content-start overflow-y-auto p-2">
                  {/* Group spots by zone */}
                  {zones.map(zone => {
                    const zoneSpots = filteredSpots.filter(
                      spot => spot.type === 'yard' && (spot as YardSpot).zoneId === zone.id
                    ).sort((a, b) => a.order_number - b.order_number); // Sort by order_number within zone
                    
                    if (zoneSpots.length === 0) return null;
                    
                    return (
                      <div 
                        key={zone.id} 
                        className="rounded-lg p-3" 
                        style={{ backgroundColor: `${zone.color}20`, borderColor: zone.color, borderWidth: '2px' }}
                      >
                        <h4 className="font-bold mb-2" style={{ color: zone.color }}>{zone.name}</h4>
                        <div className="grid grid-cols-5 gap-3">
                          {zoneSpots.map(spot => {
                            const isOccupied = occupiedSpotIds.has(spot.id);
                            return (
                            <Tooltip 
                              key={spot.id} 
                              title={isOccupied ? `${getSpotTooltip(spot)} - Occupied` : getSpotTooltip(spot)}
                              arrow
                            >
                              <div
                                className={`cursor-pointer flex items-center justify-center text-white font-bold 
                                  ${isOccupied ? 'bg-gray-500' : getStatusColor(spot.status)} w-14 h-10 rounded relative`}
                                onClick={() => handleSpotClick(spot)}
                              >
                                {spot.number}
                                {spotHasLock(spot.id) && (
                                  <Lock 
                                    className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                    style={{ fontSize: '14px' }} 
                                  />
                                )}
                              </div>
                            </Tooltip>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Staging Area - New addition */}
              

              {/* Warehouse with dock doors on top */}
              <div className="bg-gray-300 border-2 border-gray-600 p-4 m-4 mt-0 rounded flex flex-col">
                {/* Dock doors section - Updated to show inbound/outbound groups */}
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="text-sm font-semibold">Dock Doors</h4>
                    <div className="flex text-xs">
                      <div className="flex items-center mr-3">
                        <div className="w-3 h-3 border-2 border-blue-500 mr-1"></div>
                        <span>Inbound</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 border-2 border-orange-500 mr-1"></div>
                        <span>Outbound</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center space-x-4 overflow-x-auto p-2 border-b border-gray-400 pb-3">
                    {filteredSpots.filter(spot => spot.type === 'dock').map(spot => {
                      const occupied = occupiedDockIds.has(spot.id);
                      return (
                        <Tooltip 
                          key={spot.id} 
                          title={occupied ? `${getSpotTooltip(spot)} - Occupied (Config Locked)` : getSpotTooltip(spot)}
                          arrow
                        >
                          <div
                            className={`relative cursor-pointer flex items-center justify-center text-white font-bold 
                              ${getStatusColor(spot.status)} ${spot.type === 'dock' ? getGroupColor((spot as DockDoor).group) : ''} w-8 h-6 flex-shrink-0 ${occupied ? 'opacity-70' : ''}`}
                            onClick={() => handleSpotClick(spot)}
                          >
                            {spot.number}
                            {occupied && (
                              <div className="absolute -top-2 -right-2 text-[9px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1">Locked</div>
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
                
                {/* Warehouse label in the middle */}
                <h3 className="font-bold text-center my-2">Warehouse</h3>
                
                {/* Empty bottom section to balance the layout */}
                <div className="mt-3 pt-3">
                  {/* This space intentionally left empty for visual balance */}
                </div>
              </div>
            </>
          ) : (
            // Two-sided layout (yard above, warehouse with doors on top AND bottom)
            <>
              {/* Yard area */}
              <div className="bg-green-100 border-2 border-gray-600 p-4 m-4 mb-2 rounded flex-grow flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold text-center">Yard</h3>
                  <div className="flex text-xs gap-2">
                    {zones.map(zone => (
                      <div key={zone.id} className="flex items-center">
                        <div className="w-3 h-3 mr-1" style={{ backgroundColor: zone.color }}></div>
                        <span>{zone.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Yard zones layout */}
                <div className="flex-1 grid grid-cols-1 gap-4 auto-rows-max place-content-start overflow-y-auto p-2">
                  {/* Group spots by zone */}
                  {zones.map(zone => {
                    const zoneSpots = filteredSpots.filter(
                      spot => spot.type === 'yard' && (spot as YardSpot).zoneId === zone.id
                    ).sort((a, b) => a.order_number - b.order_number); // Sort by order_number within zone
                    
                    if (zoneSpots.length === 0) return null;
                    
                    return (
                      <div 
                        key={zone.id} 
                        className="rounded-lg p-3" 
                        style={{ backgroundColor: `${zone.color}20`, borderColor: zone.color, borderWidth: '2px' }}
                      >
                        <h4 className="font-bold mb-2" style={{ color: zone.color }}>{zone.name}</h4>
                        <div className="grid grid-cols-5 gap-3">
                          {zoneSpots.map(spot => {
                            const isOccupied = occupiedSpotIds.has(spot.id);
                            return (
                            <Tooltip 
                              key={spot.id} 
                              title={isOccupied ? `${getSpotTooltip(spot)} - Occupied` : getSpotTooltip(spot)}
                              arrow
                            >
                              <div
                                className={`cursor-pointer flex items-center justify-center text-white font-bold 
                                  ${isOccupied ? 'bg-gray-500' : getStatusColor(spot.status)} w-14 h-10 rounded relative`}
                                onClick={() => handleSpotClick(spot)}
                              >
                                {spot.number}
                                {spotHasLock(spot.id) && (
                                  <Lock 
                                    className="absolute top-0 right-0 text-orange-500 bg-white rounded-full p-0.5" 
                                    style={{ fontSize: '14px' }} 
                                  />
                                )}
                              </div>
                            </Tooltip>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
           
            </>
          )}
        </div>
        
        {/* Legend - updated with group indicators */}
        <div className="absolute bottom-4 right-4 bg-white p-2 border border-gray-300 rounded shadow-sm">
          <h4 className="font-bold text-sm mb-1">Legend:</h4>
          <div className="flex flex-col gap-1">
            <div className="text-xs font-semibold mb-1">Status:</div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 mr-2"></div>
              <span className="text-xs">Available</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-500 mr-2"></div>
              <span className="text-xs">Unavailable</span>
            </div>
            <div className="text-xs font-semibold mt-2 mb-1">Dock Doors:</div>
            <div className="flex items-center">
              <div className="w-4 h-4 border-2 border-blue-500 mr-2"></div>
              <span className="text-xs">Inbound</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 border-2 border-orange-500 mr-2"></div>
              <span className="text-xs">Outbound</span>
            </div>
            {showStagingArea && (
              <div className="flex items-center mt-2">
                <div className="w-4 h-4 bg-amber-400 mr-2 rounded-full"></div>
                <span className="text-xs">Staging Area</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Statistics */}
      <div className="flex mt-8 gap-4">
     
        <div className="w-1/4 bg-white p-3 shadow">
          <h3 className="font-bold">Yard Spots</h3>
          <p>Total: {spots.filter(spot => spot.type === 'yard').length}</p>
          <p>Available: {spots.filter(spot => spot.type === 'yard' && spot.status === 'available').length}</p>
          <p>In Use: {spots.filter(spot => spot.type === 'yard' && spot.status === 'in-use').length}</p>
          <p>Unavailable: {spots.filter(spot => spot.type === 'yard' && spot.status === 'unavailable').length}</p>
          <div className="mt-2 pt-2 border-t">
            <h4 className="font-semibold mb-1">By Zone:</h4>
            <div className="max-h-36 overflow-y-auto">
              {zones.map(zone => {
                const zoneSpots = spots.filter(
                  spot => spot.type === 'yard' && (spot as YardSpot).zoneId === zone.id
                );
                const availableCount = zoneSpots.filter(spot => spot.status === 'available').length;
                const inUseCount = zoneSpots.filter(spot => spot.status === 'in-use').length;
                
                return (
                  <div key={zone.id} className="mb-1">
                    <p className="text-sm font-medium" style={{ color: zone.color }}>{zone.name}: {zoneSpots.length}</p>
                    <p className="text-xs">Available: {availableCount} | In Use: {inUseCount}</p>
                  </div>
                );
              })}
            </div>
          </div>
          
        </div>
        
      </div>

      {/* Spot Detail Modal - updated with group selection */}
      {showSpotModal && selectedSpot && (
        <Modal
          open={showSpotModal}
          onClose={handleSpotModalClose}
          className="flex justify-center items-center"
        >
          <div className="w-96 bg-white border p-5 max-h-[80vh] overflow-y-auto relative flex flex-col">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={handleSpotModalClose}
            />
            <h1 className="text-xl mb-5">
              {selectedSpot.type === 'dock' ? 'Dock Door' : 'Yard Spot'} {selectedSpot.number}
            </h1>
            
            <div className="mb-4">
              <h2 className="font-bold mb-2">Status</h2>
              <div className="flex gap-2">
                {(['available', 'in-use', 'unavailable'] as Status[]).map(status => {
                  const isDock = selectedSpot.type === 'dock';
                  const isOccupied = isDock ? false : occupiedSpotIds.has(selectedSpot.id);
                  const disabled = isOccupied && status !== 'available';
                  return (
                    <button
                      key={status}
                      className={twMerge(
                        'px-3 py-1 rounded text-sm',
                        selectedSpot.status === status 
                          ? `${getStatusColor(status)} text-white` 
                          : 'bg-gray-200 hover:bg-gray-300',
                        disabled ? 'opacity-50 cursor-not-allowed' : ''
                      )}
                      onClick={() => { if (!disabled) handleStatusChange(status); }}
                      title={disabled ? 'Occupied - only Available allowed' : undefined}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Conditional group/zone selection based on spot type */}
            {selectedSpot.type === 'dock' ? (
              // Dock Door Group Selection
              <div className="mb-4">
                <h2 className="font-bold mb-2">Group</h2>
                <div className="flex gap-2">
                  {(['inbound', 'outbound', 'unassigned'] as DockGroup[]).map(group => (
                    <button
                      key={group}
                      className={twMerge(
                        'px-3 py-1 rounded text-sm',
                        (selectedSpot as DockDoor).group === group 
                          ? `border-2 ${group === 'inbound' ? 'border-blue-500 bg-blue-100' : 
                              group === 'outbound' ? 'border-orange-500 bg-orange-100' : 
                              'border-gray-400 bg-gray-100'}` 
                          : 'bg-gray-200 hover:bg-gray-300 border border-gray-300'
                      )}
                      onClick={() => handleDockGroupChange(selectedSpot.id, group)}
                    >
                      {group.charAt(0).toUpperCase() + group.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              // Yard Spot Zone Selection
              <div className="mb-4">
                <h2 className="font-bold mb-2">Zone</h2>
                <select
                  value={(selectedSpot as YardSpot).zoneId}
                  onChange={(e) => handleYardZoneChange(selectedSpot.id, e.target.value)}
                  className="w-full p-2 border rounded"
                >
                  {zones.map(zone => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="mt-auto pt-3 border-t flex justify-end">
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded"
                onClick={handleSpotModalClose}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Yard Edit Modal - updated with zone management */}
      <Modal
        open={showYardEditModal}
        onClose={handleYardEditClose}
        className="flex justify-center items-center"
      >
        <div className="w-[1710px] bg-white border-2 border-gray-300 shadow-2xl rounded-xl p-10 max-h-[90vh] overflow-y-auto relative flex flex-col">
          <Close
            className="absolute top-6 right-6 cursor-pointer hover:text-slate-400 text-gray-600"
            onClick={handleYardEditClose}
          />
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-semibold text-gray-800">Edit Yard Layout</h1>
            {getPendingChangesCount() > 0 && (
              <span className="text-orange-500 text-sm bg-orange-50 px-3 py-1 rounded-full border border-orange-200">
                {getPendingChangesCount()} unsaved changes
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-8">
            {/* Layout Settings */}
            <div className="mb-6">
              
              

       
              
              {/* Zone Management */}
              <div className="mt-6">
                <div className="flex justify-between items-center">
                  <h2 className="font-bold text-lg border-b pb-2">Yard Zones</h2>
                  <button 
                    className="text-sm bg-sky-600 text-white rounded px-2 py-1"
                    onClick={handleAddZone}
                  >
                    + Add Zone
                  </button>
                </div>
                
                <div className="mt-3 max-h-[350px] overflow-y-auto border rounded p-4">
                  <div className="grid grid-cols-4 gap-3">
                    {zones.map((zone, index) => {
                      const hasPendingChanges = originalZones.includes(zone.id) && pendingZoneChanges[zone.id];
                      const isStaging = zone.zone_type === 'staging';
                      
                      return (
                        <div 
                          key={zone.id} 
                          className={`border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer ${
                            hasPendingChanges ? 'border-orange-500 bg-orange-50' : 
                            isStaging ? 'border-amber-500 bg-amber-50' :
                            'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleZoneEditOpen(zone)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <div 
                                className="w-4 h-4 rounded-full border border-gray-300 mr-1" 
                                style={{ backgroundColor: zone.color }}
                              ></div>
                              {isStaging && (
                                <div className="w-3 h-3 bg-amber-400 rounded-full" title="Staging Zone"></div>
                              )}
                            </div>
                            <Edit className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </div>
                          <div className="text-sm font-medium text-gray-800 truncate" title={zone.name}>
                            {zone.name}
                            {isStaging && <span className="text-xs text-amber-600 ml-1">(Staging)</span>}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {spots.filter(spot => spot.type === 'yard' && (spot as YardSpot).zoneId === zone.id).length} spots
                          </div>
                          {hasPendingChanges && (
                            <div className="text-xs text-orange-600 mt-1">Modified</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {zones.length === 0 && (
                    <p className="text-gray-500 text-sm italic text-center py-8">No zones created yet</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Spot Management - only yard spots */}
            <div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium">Yard Spots</h3>
                  <button 
                    className="text-sm bg-sky-600 text-white rounded px-2 py-1"
                    onClick={handleAddYardSpot}
                    disabled={zones.length === 0}
                    title={zones.length === 0 ? "Create a zone first" : "Add new yard spot"}
                  >
                    + Add Spot
                  </button>
                </div>
                <div className="max-h-[500px] overflow-y-auto border rounded p-4">
                  {zones.map(zone => {
                    const zoneSpots = spots.filter(spot => spot.type === 'yard' && (spot as YardSpot).zoneId === zone.id)
                      .sort((a, b) => a.order_number - b.order_number);
                    
                    if (zoneSpots.length === 0) return null;
                    
                    return (
                      <div key={zone.id} className="mb-6">
                        <div className="flex items-center mb-3 pb-2 border-b">
                          <div 
                            className="w-4 h-4 rounded-full mr-2" 
                            style={{ backgroundColor: zone.color }}
                          ></div>
                          <h4 className="font-medium text-gray-800">{zone.name}</h4>
                          <span className="ml-2 text-sm text-gray-500">({zoneSpots.length} spots)</span>
                          <span className="ml-auto text-xs text-blue-600">Drag within zone to reorder</span>
                        </div>
                        <div className="grid grid-cols-6 gap-3">
                          {zoneSpots.map(spot => {
                            const hasPendingChanges = pendingSpotChanges[spot.id];
                            const isDragging = draggedSpot?.id === spot.id;
                            const isOccupied = occupiedSpotIds.has(spot.id);
                            
                            return (
                              <div 
                                key={spot.id} 
                                className={`relative border rounded-lg p-3 transition-all ${
                                  hasPendingChanges ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                                } ${isDragging ? 'opacity-40 transform scale-105 border-blue-500' : ''} ${isOccupied ? 'opacity-60' : 'hover:shadow-md'} ${isOccupied ? 'cursor-not-allowed' : 'cursor-move'}`}
                                draggable={!isOccupied}
                                onDragStart={(e) => { if (!isOccupied) handleSpotDragStart(e, spot); }}
                                onDragEnd={handleSpotDragEnd}
                                onDragOver={(e) => { if (!isOccupied) handleSpotDragOver(e); }}
                                onDrop={(e) => { if (!isOccupied) handleSpotDrop(e, spot); }}
                                onClick={() => { if (!isOccupied) handleSpotEditOpen(spot as YardSpot); }}
                                title={isOccupied ? 'Occupied - configuration changes locked' : undefined}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="w-3 h-3 bg-gray-300 rounded-full flex items-center justify-center">
                                    <span className="text-xs">⋮⋮</span>
                                  </div>
                                  <Edit className={`w-4 h-4 ${isOccupied ? 'text-gray-300' : 'text-gray-400 hover:text-gray-600'}`} />
                                </div>
                                <div className="text-sm font-medium text-gray-800 truncate" title={spot.number}>
                                  {spot.number}
                                </div>
                                {hasPendingChanges && (
                                  <div className="text-xs text-orange-600 mt-1">Modified</div>
                                )}
                                {isOccupied && (
                                  <div className="absolute top-2 right-2 text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-2 py-0.5">
                                    Occupied - Config Locked
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {spots.filter(spot => spot.type === 'yard').length === 0 && (
                    <p className="text-gray-500 text-sm italic text-center py-8">No yard spots created yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-auto pt-5 border-t flex justify-end gap-3">
            <button
              className="bg-gray-200 hover:bg-gray-300 p-2 px-4 rounded"
              onClick={handleYardEditClose}
              disabled={savingChanges}
            >
              Cancel
            </button>
            <button
              className={`bg-sky-600 hover:bg-sky-500 text-white p-2 px-4 rounded flex items-center ${savingChanges ? 'opacity-70' : ''}`}
              onClick={handleYardEditSave}
              disabled={savingChanges}
            >
              {savingChanges ? (
                <>
                  <CircularProgress size={16} color="inherit" className="mr-2" />
                  Saving...
                </>
              ) : (
                <>Save Changes {getPendingChangesCount() > 0 && `(${getPendingChangesCount()})`}</>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Spot Edit Modal */}
      {showSpotEditModal && editingYardSpot && (
        <Modal
          open={showSpotEditModal}
          onClose={handleSpotEditClose}
          className="flex justify-center items-center"
        >
          <div className="w-96 bg-white border-2 border-gray-300 shadow-xl rounded-lg p-6 relative flex flex-col">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400 text-gray-600"
              onClick={handleSpotEditClose}
            />
            <h1 className="text-xl font-semibold text-gray-800 mb-5">Edit Yard Spot</h1>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Spot Name</label>
              <input
                type="text"
                value={editingYardSpot.number}
                onChange={(e) => setEditingYardSpot({ ...editingYardSpot, number: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="Enter spot name"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Zone Assignment</label>
              <select 
                value={editingYardSpot.zoneId}
                onChange={(e) => setEditingYardSpot({ ...editingYardSpot, zoneId: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                {zones.map(zone => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name}
                  </option>
                ))}
              </select>
              <div className="flex items-center mt-2">
                <span 
                  className="w-4 h-4 rounded-full mr-2" 
                  style={{ backgroundColor: zones.find(z => z.id === editingYardSpot.zoneId)?.color || '#cccccc' }}
                ></span>
                <span className="text-sm text-gray-600">
                  {zones.find(z => z.id === editingYardSpot.zoneId)?.name || 'No zone selected'}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={editingYardSpot.status || 'available'}
                onChange={(e) => {
                  const newStatus = e.target.value as Status;
                  
                  // Check if we're changing to/from unavailable and if it differs from original
                  const isChangingToUnavailable = newStatus === 'unavailable';
                  const isChangingFromUnavailable = editingYardSpot.status === 'unavailable' && newStatus !== 'unavailable';
                  const isDifferentFromOriginal = newStatus !== originalYardSpotStatus;
                  
                  if ((isChangingToUnavailable || isChangingFromUnavailable) && isDifferentFromOriginal) {
                    const confirmed = window.confirm('Are you sure?');
                    if (confirmed) {
                      setEditingYardSpot({ ...editingYardSpot, status: newStatus });
                    }
                  } else {
                    setEditingYardSpot({ ...editingYardSpot, status: newStatus });
                  }
                }}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                <option value="available">Available</option>
                {/* Disallow selecting non-available when occupied */}
                <option value="unavailable" disabled={occupiedSpotIds.has(editingYardSpot.id)}>Unavailable</option>
                <option value="in-use" disabled={occupiedSpotIds.has(editingYardSpot.id)}>In Use</option>
                <option value="maintenance" disabled={occupiedSpotIds.has(editingYardSpot.id)}>Maintenance</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {occupiedSpotIds.has(editingYardSpot.id)
                  ? 'Occupied - only Available can be set while a trailer is present'
                  : 'Set yard spot availability status'}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={editingYardSpot.notes || ''}
                onChange={(e) => setEditingYardSpot({ ...editingYardSpot, notes: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="Enter notes about this yard spot..."
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional notes about this yard spot
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-md transition-colors"
                onClick={handleSpotEditClose}
              >
                Cancel
              </button>
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-md transition-colors"
                onClick={handleSpotEditSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Zone Edit Modal */}
      {showZoneEditModal && editingZone && (
        <Modal
          open={showZoneEditModal}
          onClose={handleZoneEditClose}
          className="flex justify-center items-center"
        >
          <div className="w-96 bg-white border-2 border-gray-300 shadow-xl rounded-lg p-6 relative flex flex-col">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400 text-gray-600"
              onClick={handleZoneEditClose}
            />
            <h1 className="text-xl font-semibold text-gray-800 mb-5">Edit Yard Zone</h1>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Zone Name</label>
              <input
                type="text"
                value={editingZone.name}
                onChange={(e) => setEditingZone({ ...editingZone, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="Enter zone name"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Zone Type</label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="zone_type"
                    value="default"
                    checked={editingZone.zone_type === 'default'}
                    onChange={(e) => setEditingZone({ ...editingZone, zone_type: e.target.value as 'default' | 'staging' })}
                    className="mr-2 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-sm text-gray-700">Default Zone</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="zone_type"
                    value="staging"
                    checked={editingZone.zone_type === 'staging'}
                    onChange={(e) => setEditingZone({ ...editingZone, zone_type: e.target.value as 'default' | 'staging' })}
                    className="mr-2 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Staging Zone</span>
                </label>
              </div>
              {editingZone.zone_type === 'staging' && (
                <p className="text-xs text-amber-600 mt-1">
                  Only one zone can be designated as staging. Setting this will make all other zones default.
                </p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Zone Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={editingZone.color}
                  onChange={(e) => setEditingZone({ ...editingZone, color: e.target.value })}
                  className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
                />
                <div className="flex-1">
                  <input
                    type="text"
                    value={editingZone.color}
                    onChange={(e) => setEditingZone({ ...editingZone, color: e.target.value })}
                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    placeholder="#000000"
                  />
                </div>
              </div>
              <div className="flex items-center mt-2">
                <span 
                  className="w-4 h-4 rounded-full mr-2 border border-gray-300" 
                  style={{ backgroundColor: editingZone.color }}
                ></span>
                <span className="text-sm text-gray-600">Preview</span>
              </div>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <div className="text-sm text-gray-700">
                <strong>Zone Statistics:</strong>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {spots.filter(spot => spot.type === 'yard' && (spot as YardSpot).zoneId === editingZone.id).length} yard spots assigned
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-md transition-colors"
                onClick={handleZoneEditClose}
              >
                Cancel
              </button>
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-md transition-colors"
                onClick={handleZoneEditSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Yard Spot Status Change Modal */}
      {showSpotStatusModal && selectedSpot && (
        <Modal
          open={showSpotStatusModal}
          onClose={handleSpotStatusModalClose}
          className="flex justify-center items-center"
        >
          <div className="w-[500px] bg-white border-2 border-gray-300 shadow-xl rounded-lg p-6 relative flex flex-col max-h-[80vh] overflow-y-auto">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400 text-gray-600"
              onClick={handleSpotStatusModalClose}
            />
            <h1 className="text-xl font-semibold text-gray-800 mb-5">Change Yard Spot {selectedSpot.number} Status</h1>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={selectedSpot.status}
                onChange={(e) => setSelectedSpot({ ...selectedSpot, status: e.target.value as Status })}
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              >
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                value={spotNotesValue}
                onChange={(e) => setSpotNotesValue(e.target.value)}
                placeholder="Add notes for this yard spot..."
                className="w-full border border-gray-300 rounded-md p-2 h-20 text-sm focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-vertical"
              />
            </div>

            {/* Locks Section */}
            {loadingLocks ? (
              <div className="mb-4 p-3 border border-gray-200 rounded-md">
                <CircularProgress size={20} />
                <span className="ml-2 text-sm text-gray-600">Loading locks...</span>
              </div>
            ) : spotLocks.length > 0 ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Lock fontSize="small" className="text-orange-600" />
                  Active Locks ({spotLocks.length})
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {spotLocks.map((lock, index) => (
                    <div key={index} className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-gray-900">{lock.reason || 'Task lock'}</p>
                            {lock.model_name && (
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                                {lock.model_name}
                              </span>
                            )}
                          </div>
                          {lock.resource && (lock.resource.name || lock.resource.trailerNumber) && (
                            <p className="text-xs text-gray-600">Resource: {lock.resource.name || lock.resource.trailerNumber}</p>
                          )}
                          {lock.task_uuid && (
                            <p className="text-xs text-gray-600 mt-1">Task: {lock.task_uuid.substring(0, 8)}...</p>
                          )}
                          {lock.created_at && (
                            <p className="text-xs text-gray-500">Created: {new Date(lock.created_at).toLocaleString()}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleClearLock(lock.resource_uuid, lock.task_uuid)}
                          disabled={clearingLock}
                          className="ml-2 px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded flex items-center gap-1 disabled:bg-gray-400"
                        >
                          {clearingLock ? <CircularProgress size={12} color="inherit" /> : <LockOpen fontSize="small" />}
                          Clear
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded"
                onClick={handleSpotStatusModalClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded"
                onClick={handleSpotStatusSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default YardManagement; 